import * as React from "react";

import { getEntitlements } from "@/api/client";

/**
 * "Does this user hold feature X?", answered from the backend's own entitlement view.
 *
 * <p>Two things this deliberately is not. It is not a tier comparison: the SPA never hardcodes
 * which tiers get what, so an admin flipping a toggle at /admin/plans changes the UI without a
 * deploy. And it is not enforcement: every gated capability is refused server-side as well, and the
 * backend does not trust this answer. Hiding a page the API would refuse anyway is a courtesy, not
 * a lock.</p>
 *
 * <p>One request per page load, shared by every caller through a module-level promise, because the
 * nav renders on every route and a fetch per consumer would be silly. It is deliberately NOT cached
 * across reloads: an upgrade should take effect on the next navigation, not after a hard refresh.</p>
 */
let inFlight: Promise<Set<string>> | null = null;

function loadFeatures(): Promise<Set<string>> {
  if (inFlight == null) {
    inFlight = getEntitlements()
      .then((e) => new Set(e.features))
      // Fail OPEN on a transport error. A blip must not blank the nav for someone who has paid;
      // the server still refuses anything they are not entitled to, so the cost of being wrong
      // this way is a link that 402s, not access they should not have.
      .catch(() => null as unknown as Set<string>);
  }
  return inFlight;
}

/** Testing seam: drop the shared promise so the next hook call refetches. */
export function resetFeatureCache(): void {
  inFlight = null;
}

export type FeatureState = "loading" | "granted" | "denied";

export function useFeature(feature: string): FeatureState {
  const [features, setFeatures] = React.useState<Set<string> | null | undefined>(undefined);

  React.useEffect(() => {
    let cancelled = false;
    void loadFeatures().then((f) => {
      if (!cancelled) setFeatures(f);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (features === undefined) return "loading";
  // null = the fetch failed. Fail open, per the note above.
  if (features === null) return "granted";
  return features.has(feature) ? "granted" : "denied";
}
